// src/pages/Page2.js
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Button, Stack, IconButton, Alert } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";

import ScheduleTable from "../components/ScheduleTable";

import NewScheduleButton from "../components/NewScheduleButton";
import EditParticipantDialog from "../components/EditParticipantDialog";
import EditTitleDialog from "../components/EditTitleDialog";
import useScheduleLogic from "../hooks/useScheduleLogic";
import { Lightbulb } from "lucide-react";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import ParticipantHistoryDialog from "../components/ParticipantHistoryDialog";

function Page2({ onSaveSchedule, userSchedules = [], isGuest = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state: defaults = {} } = useLocation();

  // Add mobile detection state
  const [isMobile, setIsMobile] = useState(false);

  const {
    participants,
    schedule,
    holidays,
    handleAutocomplete,
    handleClear,
    handleClearAll,
    handleCellRightClick,
    toggleHoliday,
    editIndex,
    setEditIndex,
    editData,
    openEditParticipant,
    handleEditChange,
    saveEdit,
    goPrev,
    goNext,
    handleMouseDownLeft,
    handleMouseEnterLeft,
    handleMouseUpLeft,
    totalHolidayShifts,
    editingSchedule,
    isEditing,
    people,
    days,
    maxShiftsPerDay,
    maxShiftsPerPerson,
    minEmptyDaysBetweenShifts,
    shifts,
    title,
    handleSaveTitle,
    handleOpenTitleDialog,
    handleCloseTitleDialog,
    isTitleDialogOpen,
    handleExport,
    // New participant history functionality
    participantHistory,
    isParticipantHistoryOpen,
    handleOpenParticipantHistory,
    handleCloseParticipantHistory,
    handleAddParticipantsFromHistory,
    handleReorderParticipants,
    toggleUnavailableCell,
  } = useScheduleLogic(defaults, onSaveSchedule, userSchedules);

  // Force landscape orientation on mobile and detect mobile device
  useEffect(() => {
    const checkIfMobile = () => {
      const mobileUserAgent =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent,
        );
      const touchDevice =
        "ontouchstart" in window || navigator.maxTouchPoints > 0;
      const isMobileDevice = mobileUserAgent || touchDevice;

      setIsMobile(isMobileDevice);

      return isMobileDevice;
    };

    const isMobileDevice = checkIfMobile();

    if (isMobileDevice) {
      // Request screen orientation lock to landscape (if supported)
      if (window.screen.orientation && window.screen.orientation.lock) {
        window.screen.orientation.lock("landscape").catch((err) => {
          console.log(t("couldNotLockOrientation"), err);
        });
      }

      // Add CSS to encourage landscape viewing
      const style = document.createElement("style");
      style.textContent = `
        @media screen and (orientation: portrait) and (max-width: 768px) {
          body::before {
            content: ${JSON.stringify(t("rotateDeviceLandscape"))};
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            background: #ff9800;
            color: white;
            padding: 10px;
            text-align: center;
            z-index: 9999;
            font-size: 14px;
            font-weight: bold;
          }
          
          .main-content {
            margin-top: 50px;
          }
        }
      `;
      document.head.appendChild(style);

      // Cleanup function
      return () => {
        document.head.removeChild(style);
        if (window.screen.orientation && window.screen.orientation.unlock) {
          window.screen.orientation.unlock();
        }
      };
    }

    // Add resize listener to re-check mobile status
    const handleResize = () => checkIfMobile();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [t]);

  const handleSave = async () => {
    const totalShifts = participants.reduce((acc, _, pIndex) => {
      return (
        acc +
        Object.values(schedule[pIndex]).filter((val) => val && val !== "-")
          .length
      );
    }, 0);

    const totalHolidayShifts = participants.reduce((acc, _, pIndex) => {
      return (
        acc +
        Object.entries(schedule[pIndex]).filter(
          ([dayIndex, val]) =>
            val && val !== "-" && holidays.includes(Number(dayIndex)),
        ).length
      );
    }, 0);

    const scheduleData = {
      date: new Date().toISOString().slice(0, 10),
      title,
      schedule,
      participants,
      holidays,
      numPeople: people,
      numDays: days,
      maxShiftsPerDay,
      maxShiftsPerPerson,
      minEmptyDaysBetweenShifts,
      shifts,
      summary: {
        totalShifts,
        totalHolidayShifts,
        totalPeople: people,
        totalDays: days,
        averageShiftsPerPerson:
          people > 0 ? (totalShifts / people).toFixed(1) : 0,
      },
    };

    if (isEditing && editingSchedule?.id?.length > 10) {
      scheduleData.id = editingSchedule.id;
    }
    try {
      await onSaveSchedule(scheduleData);
      navigate("/home");
    } catch (e) {
      alert(t("Save failed. Your changes are still on screen."));
    }
  };

  return (
    <Box
      className="main-content"
      sx={{
        p: 3,
        maxWidth: 1500,
        mx: "auto",
        backgroundColor: "#F8F8F8",
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center", // vertical center
          justifyContent: "center", // horizontal center
          mb: 1,
        }}
      >
        <h1 style={{ marginRight: 8 }}>{title}</h1>
        <IconButton
          onClick={handleOpenTitleDialog}
          size="small"
          aria-label={t("editTitle")}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>
      {isGuest && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t(
            "Guest mode: nothing is saved. Use Export to keep your work, or sign in to save.",
          )}
        </Alert>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          mb: 2,
          p: 2,
          borderRadius: 3,
          backgroundColor: "background.paper",
          boxShadow: 1,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* Left icon / label */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: "bold",
            color: "primary.main",
            minWidth: "70px",
          }}
        >
          <LightbulbOutlinedIcon size={18} />
          {t("Tips")}
        </Box>

        {/* Right list - Update tips based on mobile/desktop */}
        <Box component="ul" sx={{ m: 0, pl: 2, listStyle: "none" }}>
          <li>
            <Lightbulb size={18} style={{ marginRight: 8 }} />
            {t("Left click: toggle unavailable (click or drag)")}
          </li>
          <li>
            <Lightbulb size={18} style={{ marginRight: 8 }} />
            {isMobile
              ? t("Double tap: assign/clear shift")
              : t("Right click: assign/clear shift")}
          </li>
          <li>
            <Lightbulb size={18} style={{ marginRight: 8 }} />
            {t("Left click on the days to mark it as holiday")}
          </li>
        </Box>
      </Box>

      <ScheduleTable
        participants={participants}
        schedule={schedule}
        days={days}
        holidays={holidays}
        openEditParticipant={openEditParticipant}
        toggleHoliday={toggleHoliday}
        toggleUnavailableCell={toggleUnavailableCell} // Add this line
        handleCellRightClick={handleCellRightClick}
        handleMouseDownLeft={handleMouseDownLeft}
        handleMouseEnterLeft={handleMouseEnterLeft}
        handleMouseUpLeft={handleMouseUpLeft}
        totalHolidayShifts={totalHolidayShifts}
        shifts={shifts}
        handleClear={handleClear}
        handleClearAll={handleClearAll}
        handleAutocomplete={handleAutocomplete}
        handleExport={handleExport}
        onOpenParticipantHistory={handleOpenParticipantHistory}
        isMobile={isMobile}
        onReorderParticipants={handleReorderParticipants}
      />

      <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 2 }}>
        <NewScheduleButton />
        {!isGuest && (
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            color="secondary"
          >
            {t("Save")}
          </Button>
        )}
      </Stack>

      <EditParticipantDialog
        editIndex={editIndex}
        editData={editData}
        setEditIndex={setEditIndex}
        handleEditChange={handleEditChange}
        saveEdit={saveEdit}
        goPrev={goPrev}
        goNext={goNext}
      />
      <ParticipantHistoryDialog
        open={isParticipantHistoryOpen}
        onClose={handleCloseParticipantHistory}
        participantHistory={participantHistory}
        onAddParticipants={handleAddParticipantsFromHistory}
        currentParticipants={participants}
      />
      <EditTitleDialog
        open={isTitleDialogOpen}
        onClose={handleCloseTitleDialog}
        currentTitle={title}
        onSave={handleSaveTitle}
      />
    </Box>
  );
}

export default Page2;
